/* eslint-disable no-new-func */
import React, {useState, useCallback, useEffect, useRef} from 'react'
import CodeMirror from '@uiw/react-codemirror'
import {javascript} from '@codemirror/lang-javascript'
import {css} from '@codemirror/lang-css'
import {html} from '@codemirror/lang-html'
import {ScriptStyleEditorTypes} from './ScriptStyleEditorTypes'

const ScriptStyleEditor: React.FC<ScriptStyleEditorTypes> = ({
  id = 'js-cm-wrap',
  name = 'js',
  value = '',
  error,
  label,
  register,
  isRequired,
  disabled = false,
  className = '',
  editorClass = '',
  height = '300px',
  onChange,
  onBlur,
  editorRef,
  isReadOnly = false,
  labelClass = '',
  placeholder = 'Enter only script and style tags here...',
  onValidChange,
  ...rest
}) => {
  const [internalValue, setInternalValue] = useState(value)
  const [validationError, setValidationError] = useState<string>('')
  const [touched, setTouched] = useState(false)
  const hiddenInputRef = useRef(null)

  // Validate content
  const validateContent = useCallback(
    (content: string) => {
      if (!content) {
        return isRequired ? 'This field is required' : ''
      }

      // Check for any content outside of allowed tags
      const contentWithoutTags = content
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<!--[\s\S]*?-->/g, '')
        .trim()

      if (contentWithoutTags.length > 0) {
        return 'Only <script> and <style> tags are allowed'
      }

      // Split content into individual tags for validation
      const tags = content.match(/<[^>]+>/g) || []
      const openScriptTags = []
      const openStyleTags = []

      for (const tag of tags) {
        // Check for malformed tags
        if (!tag.endsWith('>')) {
          return 'Found unclosed HTML tag'
        }

        const isClosingTag = tag.startsWith('</')
        const tagName = tag.match(/<\/?([a-z]+)/i)?.[1]?.toLowerCase()

        if (!tagName) {
          return 'Invalid HTML tag format'
        }

        if (tagName !== 'script' && tagName !== 'style') {
          return 'Only <script> and <style> tags are allowed'
        }

        if (tagName === 'script') {
          if (isClosingTag) {
            if (openScriptTags.length === 0) {
              return 'Found closing </script> tag without matching opening tag'
            }
            openScriptTags.pop()
          } else {
            // Validate script tag attributes
            const typeAttr = tag.match(/type=["']([^"']*)["']/i)
            if (typeAttr && !typeAttr[1].includes('javascript')) {
              return 'Script type must be javascript or text/javascript'
            }
            openScriptTags.push(tag)
          }
        }

        if (tagName === 'style') {
          if (isClosingTag) {
            if (openStyleTags.length === 0) {
              return 'Found closing </style> tag without matching opening tag'
            }
            openStyleTags.pop()
          } else {
            openStyleTags.push(tag)
          }
        }
      }

      if (openScriptTags.length > 0) {
        return 'Unclosed <script> tag found'
      }

      if (openStyleTags.length > 0) {
        return 'Unclosed <style> tag found'
      }

      // Validate script content
      const scriptMatches = content.match(/<script[^>]*>([\s\S]*?)<\/script>/gi) || []
      for (const script of scriptMatches) {
        const scriptContent = script.replace(/<script[^>]*>|<\/script>/gi, '').trim()
        try {
          // Basic JavaScript syntax validation
          new Function(scriptContent)
        } catch (error: any) {
          return `JavaScript syntax error: ${error.message || 'Invalid syntax'}`
        }
      }

      // Validate style content
      const styleMatches = content.match(/<style[^>]*>([\s\S]*?)<\/style>/gi) || []
      for (const style of styleMatches) {
        const styleContent = style.replace(/<style[^>]*>|<\/style>/gi, '').trim()
        if (styleContent) {
          // Basic CSS validation (check for unclosed braces)
          const openBraces = (styleContent.match(/{/g) || []).length
          const closeBraces = (styleContent.match(/}/g) || []).length
          if (openBraces !== closeBraces) {
            return 'CSS syntax error: Unclosed braces in style content'
          }
        }
      }

      return ''
    },
    [isRequired]
  )

  // Handle content change
  const handleChange = useCallback(
    (value: string) => {
      setInternalValue(value)
      setTouched(true)
      const error = validateContent(value)
      setValidationError(error)
      if (onValidChange) {
        onValidChange(!error)
      }
      if (onChange) {
        onChange(value)
      }
    },
    [onChange, validateContent, onValidChange]
  )

  // Handle blur event
  const handleBlur = useCallback(() => {
    setTouched(true)
    if (onBlur) {
      onBlur()
    }
  }, [onBlur])

  // Update internal value when prop changes (without marking as touched)
  useEffect(() => {
    setInternalValue(value)
    const error = validateContent(value)
    setValidationError(error)
    if (onValidChange) {
      onValidChange(!error)
    }
  }, [value, validateContent, onValidChange])

  // Register with react-hook-form if provided
  useEffect(() => {
    if (register) {
      const {onChange: regOnChange, onBlur: regOnBlur} = register(name)
      if (regOnChange) {
        regOnChange({target: {value: internalValue}})
      }
      if (regOnBlur) {
        regOnBlur()
      }
    }
  }, [register, name, internalValue])

  return (
    <div className={`fv-row ${className}`}>
      {label && (
        <label className={`form-label ${labelClass}`}>
          {label}
          {isRequired && <span className='required'>&nbsp;*</span>}
        </label>
      )}

      <div className={`a-demo-cm6 cm-editor-wrap`}>
        <input
          type='hidden'
          name={name}
          id={`playground-${name}`}
          value={value}
          ref={hiddenInputRef}
          data-lang='javascript'
          data-min-height='100px'
          data-max-height='400px'
          readOnly
        />
        <CodeMirror
          value={internalValue}
          extensions={[html(), javascript(), css()]}
          onChange={handleChange}
          onBlur={handleBlur}
          editable={!disabled && !isReadOnly}
          placeholder={placeholder}
          ref={editorRef}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            highlightActiveLine: true,
            highlightSelectionMatches: true,
            autocompletion: true,
          }}
          height='100%'
        />
      </div>

      {touched && (error || validationError) && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
            <span role='alert'>{error || validationError}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default ScriptStyleEditor
